import { useEffect, useRef, useState } from "react";
import { Radar, Loader2, ShieldAlert, Copy, ChevronDown, ChevronUp, Sparkles, FileDown, AlertTriangle } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const SENTIMENT_COLOR = { positive: "text-truth-green", neutral: "text-amber-400", negative: "text-sovereign" };
const RISK_COLOR = { low: "text-truth-green", medium: "text-amber-400", high: "text-sovereign" };
const PROVIDER_LABEL = { openai: "OpenAI", claude: "Claude", gemini: "Gemini", perplexity: "Perplexity" };
const POLL_INTERVAL_MS = 3000;

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg || String(d)).join(", ");
  return fallback;
};

export const SovereignPulsePanel = ({ auditId }) => {
  const [targetType, setTargetType] = useState("brand");
  const [targetName, setTargetName] = useState("");
  const [groundTruth, setGroundTruth] = useState("");
  const [showGroundTruth, setShowGroundTruth] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const pollRef = useRef(null);

  const fetchHistory = () => {
    axios.get(`${API}/audits/${auditId}/sovereign-pulse`).then((res) => setHistory(res.data)).catch(() => {});
  };

  useEffect(fetchHistory, [auditId]);
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const pollRun = (runId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/audits/${auditId}/sovereign-pulse/${runId}`);
        setResult(res.data);
        if (res.data.status !== "running") {
          clearInterval(pollRef.current);
          setRunning(false);
          fetchHistory();
          if (res.data.status === "failed") {
            toast.error(`Sovereign Pulse run failed: ${res.data.error || "unknown error"}`);
          } else if (res.data.alert_triggered) {
            toast.error("Clinical Threshold breached - Emergency Protocol triggered.");
          } else if (res.data.analysis?.correction_needed) {
            toast.warning("Reputation Correction draft generated for your review.");
          } else {
            toast.success("Sovereign Pulse complete.");
          }
        }
      } catch (err) {
        clearInterval(pollRef.current);
        setRunning(false);
        toast.error("Lost connection while checking Sovereign Pulse status.");
      }
    }, POLL_INTERVAL_MS);
  };

  const runPulse = async () => {
    if (!targetName.trim()) {
      toast.error("Enter a personal or business name to probe.");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/audits/${auditId}/sovereign-pulse`, {
        target_type: targetType,
        target_name: targetName.trim(),
        ground_truth: groundTruth.trim() || null,
      });
      setResult(res.data);
      fetchHistory();
      if (res.data.status === "running") {
        pollRun(res.data.id);
      } else {
        setRunning(false);
      }
    } catch (err) {
      setRunning(false);
      if (err?.response?.status === 402) {
        toast.error(getErrorMessage(err, "Scan limit reached - please upgrade your plan."));
      } else {
        toast.error(getErrorMessage(err, "Could not run Sovereign Pulse."));
      }
    }
  };

  const copyDraft = () => {
    const d = result?.correction_draft;
    if (!d) return;
    navigator.clipboard.writeText(`${d.subject}\n\n${d.correction_statement}\n\nRecommended action: ${d.recommended_action}`);
    toast.success("Correction draft copied.");
  };

  const downloadCorrectionLetter = () => {
    window.open(`${API}/audits/${auditId}/sovereign-pulse/${result.id}/correction-letter.pdf`, "_blank");
  };

  return (
    <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="sovereign-pulse-panel">
      <div className="mb-1 flex items-center gap-2">
        <Radar className="h-5 w-5 text-warning-amber" />
        <h2 className="font-heading text-xl font-bold text-white">Sovereign Pulse &mdash; Multi-AI Reputation Probe</h2>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Queries OpenAI, Claude, Gemini &amp; Perplexity in parallel for sentiment and reputation risk. Personal name and
        business name can be probed independently.
      </p>

      <div className="mb-3 flex gap-2">
        <Button
          size="sm"
          variant={targetType === "personal" ? "default" : "outline"}
          onClick={() => setTargetType("personal")}
          className={targetType === "personal" ? "bg-warning-amber text-obsidian hover:bg-warning-amber/90" : "border-surface-border text-slate-300"}
          data-testid="pulse-target-type-personal-btn"
        >
          Personal Name
        </Button>
        <Button
          size="sm"
          variant={targetType === "brand" ? "default" : "outline"}
          onClick={() => setTargetType("brand")}
          className={targetType === "brand" ? "bg-warning-amber text-obsidian hover:bg-warning-amber/90" : "border-surface-border text-slate-300"}
          data-testid="pulse-target-type-brand-btn"
        >
          Business Name
        </Button>
      </div>

      <Input
        placeholder={targetType === "personal" ? "e.g. Jane Doe" : "e.g. Paradox Unlimited"}
        value={targetName}
        onChange={(e) => setTargetName(e.target.value)}
        className="mb-2 border-surface-border bg-obsidian text-white"
        data-testid="pulse-target-name-input"
      />

      <button
        onClick={() => setShowGroundTruth((v) => !v)}
        className="mb-2 flex items-center gap-1 text-xs text-slate-400 hover:text-white"
        data-testid="pulse-ground-truth-toggle"
      >
        {showGroundTruth ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Add Ground-Truth Facts (optional)
      </button>
      {showGroundTruth && (
        <Textarea
          value={groundTruth}
          onChange={(e) => setGroundTruth(e.target.value)}
          placeholder="Paste authoritative bio/company facts to check AI claims against. Leave blank to skip factual-accuracy scoring - sentiment and risk detection still run."
          rows={4}
          className="mb-3 border-surface-border bg-obsidian text-white"
          data-testid="pulse-ground-truth-textarea"
        />
      )}

      <Button
        onClick={runPulse}
        disabled={running}
        className="gap-2 bg-warning-amber text-obsidian hover:bg-warning-amber/90"
        data-testid="run-sovereign-pulse-btn"
      >
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />} Run Sovereign Pulse
      </Button>

      {result?.status === "running" && (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-warning-amber/40 bg-warning-amber/5 p-4" data-testid="pulse-scan-in-progress">
          <Loader2 className="h-4 w-4 animate-spin text-warning-amber" />
          <p className="text-sm text-warning-amber">Scan in Progress &mdash; querying AI providers in parallel. This updates automatically.</p>
        </div>
      )}

      {result?.status === "failed" && (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-sovereign/50 bg-sovereign/10 p-4" data-testid="pulse-scan-failed">
          <AlertTriangle className="h-4 w-4 text-sovereign" />
          <p className="text-sm text-sovereign">Scan failed: {result.error || "unknown error"}. Please try again.</p>
        </div>
      )}

      {result?.status === "completed" && (
        <div className="mt-5 space-y-4" data-testid="pulse-result-card">
          {result.alert_triggered && (
            <div className="rounded-md border border-sovereign/50 bg-sovereign/10 p-3" data-testid="pulse-alert-banner">
              <p className="flex items-center gap-2 text-sm font-bold text-sovereign">
                <ShieldAlert className="h-4 w-4" /> Clinical Threshold Breached - Legal Team Notified
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 rounded-md border border-surface-border bg-obsidian p-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Aggregate Sentiment</p>
              <p className="text-2xl font-bold text-white" data-testid="pulse-aggregate-score">
                {result.analysis?.aggregate_sentiment_score}/100
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Risk Level</p>
              <p className={`text-lg font-bold uppercase ${RISK_COLOR[result.analysis?.aggregate_risk_level] || "text-slate-300"}`} data-testid="pulse-risk-level">
                {result.analysis?.aggregate_risk_level}
              </p>
            </div>
            {result.priority_provider && (
              <p className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="pulse-priority-provider-note">
                <Sparkles className="h-3.5 w-3.5 text-truth-green" /> Model Feedback Loop: prioritizing {PROVIDER_LABEL[result.priority_provider] || result.priority_provider} (highest tracked accuracy)
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(result.analysis?.per_provider || []).map((p) => (
              <div key={p.provider} className="rounded-md border border-surface-border bg-obsidian p-3" data-testid={`pulse-provider-card-${p.provider}`}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{PROVIDER_LABEL[p.provider] || p.provider}</span>
                  <span className={`text-xs font-bold uppercase ${SENTIMENT_COLOR[p.sentiment_label] || "text-slate-300"}`}>
                    {p.sentiment_label} ({p.sentiment_score})
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Factual accuracy: <span className="text-slate-300">{p.factual_accuracy}</span>
                </p>
                {p.accuracy_notes && <p className="mt-1 text-xs text-slate-500">{p.accuracy_notes}</p>}
                {p.reputation_risks?.length > 0 && (
                  <ul className="mt-1.5 list-inside list-disc text-xs text-sovereign">
                    {p.reputation_risks.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {result.correction_draft && (
            <div className="rounded-md border border-warning-amber/40 bg-warning-amber/5 p-4" data-testid="pulse-correction-draft-card">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-bold text-warning-amber">Reputation Correction Draft (Legal &amp; Clinical)</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyDraft} className="gap-1.5 border-surface-border text-slate-200" data-testid="copy-correction-draft-btn">
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadCorrectionLetter} className="gap-1.5 border-surface-border text-slate-200" data-testid="download-correction-letter-btn">
                    <FileDown className="h-3.5 w-3.5" /> Download Correction Letter
                  </Button>
                </div>
              </div>
              <p className="text-sm font-semibold text-white">{result.correction_draft.subject}</p>
              <p className="mt-1 text-sm text-slate-300">{result.correction_draft.correction_statement}</p>
              <p className="mt-2 text-xs text-slate-500">Recommended action: {result.correction_draft.recommended_action}</p>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setShowHistory((v) => !v)}
        className="mt-4 flex items-center gap-1 text-xs text-slate-400 hover:text-white"
        data-testid="pulse-history-toggle"
      >
        {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        View Past Runs ({history.length})
      </button>
      {showHistory && (
        <div className="mt-2 space-y-1.5" data-testid="pulse-history-list">
          {history.length === 0 ? (
            <p className="text-xs text-slate-500" data-testid="pulse-history-empty">No past Sovereign Pulse runs.</p>
          ) : (
            history.map((h, i) => (
              <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-surface-border/60 px-3 py-1.5 text-xs" data-testid={`pulse-history-item-${i}`}>
                <span className="text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</span>
                <span className="text-slate-500">{h.target_type === "personal" ? "Personal" : "Business"}: {h.target_name}</span>
                <span className="text-slate-300">{h.status === "completed" ? `${h.analysis?.aggregate_sentiment_score}/100` : h.status}</span>
                <span className={RISK_COLOR[h.analysis?.aggregate_risk_level] || "text-slate-300"}>{h.analysis?.aggregate_risk_level}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

